import React, { useEffect, useRef, useState } from 'react'
import LangSwitch from '../ui/LangSwitch';
import { hasBothLanguages, localizedText } from '../../utils/localized';
import { MwSpinnerButton } from '../ui'
import { useCurrent, useFetch } from '../../hooks'
import { getCatalogLabel, getPriorityAppearance, getPriorityLabel, getStatusAppearance, getStatusLabel } from '../../constants/ticket'
import { useTranslation } from 'react-i18next';
import { FaFilePdf, FaFileAlt, FaCalendarAlt, FaUser, FaTag, FaBoxOpen, FaTimes, FaDownload } from "react-icons/fa";
import axios from 'axios';
import { toSafeHtml } from '../../utils/richTextHtml';

/* ──────────────────────────────────────────
   File Preview Popup
────────────────────────────────────────── */
const FilePopup = ({ url, name, ext, isPdf, isImage, isVideo, previewUrl, onClose }) => {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [onClose]);

    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(8,12,20,0.82)', backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24
        }}>
            <div onClick={e => e.stopPropagation()} style={{
                background: '#fff', borderRadius: 20, overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                maxWidth: '92vw', maxHeight: '90vh', minWidth: 360,
                boxShadow: '0 32px 80px rgba(0,0,0,0.35)'
            }}>
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 20px', borderBottom: '1px solid #f1f5f9', background: '#fafafa', gap: 16
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                        <div style={{
                            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                            background: isPdf ? '#fff1f2' : '#f0f9ff',
                            color: isPdf ? '#ef4444' : '#0ea5e9',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            {isPdf ? <FaFilePdf size={16} /> : <FaFileAlt size={16} />}
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>{name}</div>
                            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>{ext} file</div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <a href={url} download={name} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8,
                            background: '#0f172a', color: '#fff', fontSize: 11, fontWeight: 700, textDecoration: 'none'
                        }}>
                            <FaDownload size={9} /> تحميل
                        </a>
                        <button onClick={onClose} style={{
                            width: 32, height: 32, borderRadius: '50%', background: '#f1f5f9',
                            border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b'
                        }}>
                            <FaTimes size={12} />
                        </button>
                    </div>
                </div>
                <div style={{
                    flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 24, background: '#f8fafc', minHeight: 220
                }}>
                    {isImage ? (
                        <img src={previewUrl || url} alt={name} style={{ maxWidth: '100%', maxHeight: '72vh', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.12)', objectFit: 'contain' }} />
                    ) : isVideo ? (
                        <video
                            controls
                            playsInline
                            style={{ width: '70vw', maxWidth: 980, maxHeight: '72vh', borderRadius: 12, background: '#000', boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}
                        >
                            <source
                                src={url}
                                type={ext === 'webm' ? 'video/webm' : ext === 'ogg' ? 'video/ogg' : 'video/mp4'}
                            />
                        </video>
                    ) : isPdf ? (
                        <iframe src={url} title={name} style={{ width: '70vw', height: '72vh', minWidth: 300, border: 'none', borderRadius: 10 }} />
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px 60px' }}>
                            <div style={{ width: 72, height: 72, borderRadius: 20, background: '#f0f9ff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                <FaFileAlt size={30} color="#0ea5e9" />
                            </div>
                            <p style={{ color: '#64748b', fontSize: 13, marginBottom: 12 }}>لا يمكن معاينة هذا الملف</p>
                            <a href={url} download target="_blank" rel="noreferrer" style={{ color: '#6366f1', fontWeight: 700, fontSize: 13 }}>تحميل الملف</a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const normalizeAttachmentList = (attachments) => (
    Array.isArray(attachments)
        ? attachments
        : attachments
            ? [attachments]
            : []
).filter(Boolean);

/* ──────────────────────────────────────────
   Main Component
────────────────────────────────────────── */
const TicketInfo = ({ item, loading, supportSettings, onRefresh }) => {
    const [t] = useTranslation('global');
    const { currentLangId, user } = useCurrent();
    const [popupOpen, setPopupOpen] = useState('');
    // لغة عرض العنوان والمحتوى لو التذكرة مكتوبة بالعربي والإنجليزي.
    const [viewLang, setViewLang] = useState(currentLangId === 'en' ? 'en' : 'ar');
    const [deleteLoading, setDeleteLoading] = useState(false);
    const currentUserId = String(user?._id || user?.id || user?.userId || user?.user?._id || user?.user?.id || user?.user?.userId || '').trim() || null;
    const createdById = item?.createdBy != null && typeof item.createdBy !== 'object' ? String(item.createdBy).trim() : null;
    const customerId = item?.customerId != null ? String(item.customerId).trim() : null;
    const shouldFetchSupportAdmins = Boolean(createdById && createdById !== customerId && (!currentUserId || createdById !== currentUserId));
    const { data: supportAdminsResult } = useFetch(
        shouldFetchSupportAdmins ? `${process.env.REACT_APP_Auth_API_BASE_URL}/users/support-admins` : null,
        Boolean(shouldFetchSupportAdmins)
    );
    const supportAdmins = Array.isArray(supportAdminsResult?.data) ? supportAdminsResult.data : [];

    const attachmentList = normalizeAttachmentList(item?.attachments);
    const attachmentUrl = attachmentList[0] || '';
    const getAttachmentInfo = (url) => {
        const originalName = url ? String(url).split('/').pop() : '';
        const name = originalName.replace(/^\d+-[a-f0-9-]+-/, '');
        const ext = (() => {
            const parts = originalName.split('.');
            if (parts.length <= 1) return '';
            let value = parts.pop().toLowerCase();
            if (!isNaN(value) && parts.length > 0) value = parts.pop().toLowerCase();
            return value;
        })();
        return {
            url,
            originalName,
            name,
            ext,
            isImage: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext),
            isVideo: ['mp4', 'webm', 'ogg'].includes(ext),
            isPdf: ext === 'pdf'
        };
    };
    const popupAttachment = getAttachmentInfo(popupOpen);
    const attachmentOriginalName = attachmentUrl ? attachmentUrl.split('/').pop() : '';
    const attachmentExt = (() => {
        const parts = attachmentOriginalName.split('.');
        if (parts.length <= 1) return '';
        let ext = parts.pop().toLowerCase();
        if (!isNaN(ext) && parts.length > 0) ext = parts.pop().toLowerCase();
        return ext;
    })();

    const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(attachmentExt);
    const [previewUrl, setPreviewUrl] = useState(null);
    const objectUrlRef = useRef(null);

    useEffect(() => {
        if (objectUrlRef.current) { URL.revokeObjectURL(objectUrlRef.current); objectUrlRef.current = null; }
        setPreviewUrl(null);
        if (!attachmentUrl || !isImage) return;
        const controller = new AbortController();
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
        fetch(attachmentUrl, { headers, signal: controller.signal })
            .then(r => r.blob()).then(blob => {
                const url = URL.createObjectURL(blob);
                objectUrlRef.current = url;
                setPreviewUrl(url);
            }).catch(() => { });
        return () => {
            controller.abort();
            if (objectUrlRef.current) { URL.revokeObjectURL(objectUrlRef.current); objectUrlRef.current = null; }
        };
    }, [attachmentUrl, isImage]);

    if (loading) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80 }}>
            <MwSpinnerButton isFullCenter withLabel={false} />
        </div>
    );

    if (!item) {
        return (
            <div style={{
                background: '#fff',
                borderRadius: 16,
                border: '1px solid #e2e8f0',
                padding: '24px',
                color: '#64748b',
                fontFamily: "'Cairo', 'Segoe UI', sans-serif",
                direction: currentLangId === 'ar' ? 'rtl' : 'ltr'
            }}>
                تعذر تحميل بيانات التذكرة
            </div>
        );
    }

    const statusValue = item?.statusId || item?.status;
    const sc = getStatusAppearance(supportSettings?.statuses, statusValue);
    const priorityMeta = getPriorityAppearance(item?.priority || 'medium');
    const ticketVisibility = String(item?.visibility || 'external').toLowerCase() === 'internal' ? 'internal' : 'external';
    const ticketTypeMeta = ticketVisibility === 'internal'
        ? {
            label: currentLangId === 'ar' ? 'داخلية' : 'Internal',
            bg: '#eef2ff',
            color: '#4f46e5',
            dot: '#6366f1'
        }
        : {
            label: currentLangId === 'ar' ? 'خارجية' : 'External',
            bg: '#ecfdf5',
            color: '#047857',
            dot: '#10b981'
        };
    const createdAtLabel = (() => {
        const date = item?.createdAt ? new Date(item.createdAt) : null;
        if (!date || Number.isNaN(date.getTime())) return '-';
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    })();
    const updatedAtLabel = (() => {
        const raw = item?.modifiedAt || item?.updatedAt || null;
        const date = raw ? new Date(raw) : null;
        if (!date || Number.isNaN(date.getTime())) return '-';
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    })();

    const createdByLabel = (() => {
        const createdByRaw = item?.createdBy;
        if (!createdByRaw) return item?.customerName || '-';
        if (typeof createdByRaw === 'object') {
            return createdByRaw?.name || createdByRaw?.fullName || createdByRaw?.username || createdByRaw?.email || createdByRaw?._id || item?.customerName || '-';
        }
        const resolvedCreatedById = String(createdByRaw || '').trim();
        const resolvedCustomerId = item?.customerId != null ? String(item.customerId).trim() : '';
        if (resolvedCreatedById && resolvedCustomerId && resolvedCreatedById === resolvedCustomerId) {
            return item?.customerName || resolvedCreatedById;
        }
        if (currentUserId && resolvedCreatedById === currentUserId) {
            return user?.name || user?.fullName || user?.username || user?.email || resolvedCreatedById;
        }
        const found = supportAdmins.find((u) => String(u?._id || '') === resolvedCreatedById);
        if (found) {
            return found?.name || found?.username || found?.email || resolvedCreatedById;
        }
        return currentLangId === 'ar' ? 'فريق الدعم الفني' : 'Support Team';
    })();

    const metaItems = [
        { icon: <FaTag size={11} />,  label: t('support.tickets.ticketNumber'), value: item.ticketNumber || '-' },
        { icon: <FaUser size={11} />, label: t('support.tickets.createdBy'),  value: createdByLabel },
        { icon: <FaTag size={11} />,  label: t('support.tickets.section'),    value: getCatalogLabel(supportSettings?.sections, item.sectionId, currentLangId) },
        { icon: <FaBoxOpen size={11} />, label: t('support.tickets.product'), value: getCatalogLabel(supportSettings?.products, item.ProductId, currentLangId) },
        { icon: <FaTag size={11} />,  label: t('support.tickets.priority'), value: getPriorityLabel(item?.priority || 'medium', currentLangId) },
        { icon: <FaCalendarAlt size={11} />, label: t('support.tickets.createdAt'), value: createdAtLabel },
        { icon: <FaCalendarAlt size={11} />, label: t('support.tickets.updatedAt'), value: updatedAtLabel },
    ];

    const canDeleteTicketAttachment = (() => {
        if (!item?._id || !attachmentList.length) return false;
        const currentUserId = user?._id || user?.id || user?.userId || null;
        if (!currentUserId) return false;
        if (user?.role === 'admin' || user?.supportAdminEnabled === true) return true;
        return String(item?.customerId || '') === String(currentUserId) || String(item?.createdBy || '') === String(currentUserId);
    })();

    const deleteTicketAttachment = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!item?._id || !attachmentList.length) return;
        if (!canDeleteTicketAttachment) return;
        setDeleteLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

            await axios.delete(
                `${process.env.REACT_APP_UPLOADS_API_BASE_URL}/delete`,
                {
                    data: attachmentList.length > 1 ? { keyFiles: attachmentList } : { keyFile: attachmentUrl },
                    headers,
                    validateStatus: () => true
                }
            );

            await axios.delete(
                `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/${item._id}/attachment`,
                {
                    headers,
                    validateStatus: () => true
                }
            );

            if (typeof onRefresh === 'function') {
                onRefresh();
            }
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <>
            {popupOpen && (
                <FilePopup url={popupAttachment.url} name={popupAttachment.name} ext={popupAttachment.ext}
                    isPdf={popupAttachment.isPdf} isImage={popupAttachment.isImage} isVideo={popupAttachment.isVideo} previewUrl={popupAttachment.url === attachmentUrl ? previewUrl : null}
                    onClose={() => setPopupOpen('')} />
            )}

            <div style={{ fontFamily: "'Cairo', 'Segoe UI', sans-serif", direction: currentLangId === 'ar' ? 'rtl' : 'ltr', display: 'flex', flexDirection: 'column', gap: 14 }}>

                {/* ── Card 1: Header – Subject + Status + Meta ── */}
                <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    {/* accent stripe */}
                    <div style={{ height: 4, background: 'linear-gradient(90deg,#6366f1,#38bdf8)' }} />

                    <div style={{ padding: '20px 24px' }}>
                        {/* Subject + status in one row */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
                            <h2 dir="auto" style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#0f172a', lineHeight: 1.4, unicodeBidi: 'plaintext', textAlign: 'start' }}>
                                {localizedText(item, 'subject', viewLang)}
                            </h2>
                            {hasBothLanguages(item, ['subject', 'content']) && (
                                <LangSwitch value={viewLang} onChange={setViewLang} isAr={currentLangId !== 'en'} filled={{ ar: true, en: true }} />
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '5px 13px', borderRadius: 100,
                                    background: sc.bg, color: sc.color, fontSize: 12, fontWeight: 700, flexShrink: 0
                                }}>
                                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: sc.dot }} />
                                    {getStatusLabel(supportSettings?.statuses, statusValue, currentLangId)}
                                </span>
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '5px 13px', borderRadius: 100,
                                    background: ticketTypeMeta.bg, color: ticketTypeMeta.color, fontSize: 12, fontWeight: 700, flexShrink: 0
                                }}>
                                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: ticketTypeMeta.dot }} />
                                    {ticketTypeMeta.label}
                                </span>
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '5px 13px', borderRadius: 100,
                                    background: priorityMeta.bg, color: priorityMeta.color, fontSize: 12, fontWeight: 700, flexShrink: 0
                                }}>
                                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: priorityMeta.dot }} />
                                    {getPriorityLabel(item?.priority || 'medium', currentLangId)}
                                </span>
                            </div>
                        </div>

                        {/* Meta grid: 4 equal tiles */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                            {metaItems.map(({ icon, label, value }, i) => (
                                <div key={i} style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '10px 14px', borderRadius: 10,
                                    background: '#f8fafc', border: '1px solid #e2e8f0'
                                }}>
                                    <div style={{
                                        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                                        background: '#eef2ff', color: '#6366f1',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        {icon}
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>{label}</div>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── Card 2: Message body + Attachment ── */}
                <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0' }}>
                    {/* label bar */}
                    <div style={{
                        padding: '12px 24px', borderBottom: '1px solid #f1f5f9',
                        fontSize: 10, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1.4
                    }}>
                        {t('support.tickets.subject') || 'محتوى التذكرة'}
                    </div>

                    <div style={{ padding: '20px 24px' }}>
                        <style>{`
                            .mw-ticket-html ol { list-style: decimal; padding-inline-start: 20px; margin: 0; }
                            .mw-ticket-html ul { list-style: disc; padding-inline-start: 20px; margin: 0; }
                            .mw-ticket-html li { margin: 7px 0; padding-inline-start: 4px; }
                            .mw-ticket-html p { margin: 0 0 10px; }
                            .mw-ticket-html p:last-child { margin-bottom: 0; }
                            .mw-ticket-html div { margin-bottom: 8px; }
                            .mw-ticket-html div:last-child { margin-bottom: 0; }
                            .mw-ticket-html a { color: #4f46e5; text-decoration: underline; }
                            .mw-ticket-html,
                            .mw-ticket-html * {
                                max-width: 100%;
                                overflow-wrap: anywhere;
                                word-break: break-word;
                                white-space: normal;
                            }
                        `}</style>
                        <div
                            className="mw-ticket-html"
                            dir="auto"
                            style={{ margin: 0, fontSize: 14, lineHeight: 1.85, color: '#334155', unicodeBidi: 'plaintext', textAlign: 'start', maxWidth: '100%', overflowX: 'hidden', whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                            dangerouslySetInnerHTML={{ __html: toSafeHtml(localizedText(item, 'content', viewLang)) }}
                        />

                        {!!attachmentList.length && (
                            <div style={{ marginTop: 22, paddingTop: 18, borderTop: '1px dashed #e2e8f0' }}>
                                <div style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1.4, marginBottom: 12 }}>
                                    {t('support.tickets.attachments')}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, maxWidth: 560 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minWidth: 0 }}>
                                        {attachmentList.map((url) => {
                                            const info = getAttachmentInfo(url);
                                            return (
                                                <button
                                                    key={url}
                                                    onClick={() => setPopupOpen(url)}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: 14,
                                                        padding: '12px 16px', borderRadius: 12,
                                                        background: '#f8fafc', border: '1.5px solid #e2e8f0',
                                                        cursor: 'pointer', textAlign: 'right',
                                                        width: '100%',
                                                        transition: 'all .15s'
                                                    }}
                                                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.background = '#f5f3ff'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#f8fafc'; }}
                                                >
                                                    {info.isImage ? (
                                                        <img src={url === attachmentUrl ? (previewUrl || url) : url} alt={info.name}
                                                            style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 9, flexShrink: 0, border: '1px solid #e2e8f0' }} />
                                                    ) : (
                                                        <div style={{
                                                            width: 48, height: 48, borderRadius: 10, flexShrink: 0,
                                                            background: info.isPdf ? '#fff1f2' : '#f0f9ff',
                                                            color: info.isPdf ? '#ef4444' : '#0ea5e9',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                        }}>
                                                            {info.isPdf ? <FaFilePdf size={20} /> : <FaFileAlt size={20} />}
                                                        </div>
                                                    )}
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {info.name}
                                                        </div>
                                                        <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 3 }}>
                                                            {info.ext} · انقر للمعاينة
                                                        </div>
                                                    </div>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                                        <path d="M15 18l-6-6 6-6" />
                                                    </svg>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {canDeleteTicketAttachment && (
                                        <button
                                            type="button"
                                            onClick={deleteTicketAttachment}
                                            disabled={deleteLoading}
                                            style={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: 999,
                                                border: '1px solid #fecaca',
                                                background: '#fff1f2',
                                                color: '#ef4444',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: deleteLoading ? 'not-allowed' : 'pointer',
                                                opacity: deleteLoading ? 0.6 : 1,
                                                flexShrink: 0
                                            }}
                                        >
                                            <FaTimes size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </>
    );
};

export default TicketInfo;
